import { useState, useMemo, useEffect, useRef } from 'react'
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  DragOverlay,
} from '@dnd-kit/core'
import { useTally } from '../hooks/useTally.jsx'
import { useTallyI18n } from '../i18n/tally.js'
import { EmojiPicker } from '../components/EmojiPicker.jsx'
import { Emoji } from '../components/Emoji.jsx'
import {
  MAX_LEVELS,
  buildTreeRows,
  canReparent,
  getCategoryPath,
  getDescendantIds,
  getDepth,
  getSubtreeDepth,
  isSystemCategory,
} from '../categoryTree.js'

const TOP_LEVEL_DROP_ID = '__top_level__'

export function Categories() {
  const t = useTallyI18n()
  const { data, addCategory, updateCategory, removeCategory } = useTally()
  const [tab, setTab]      = useState('expense')
  const [editing, setEdit] = useState(null)
  const [activeId, setActiveId] = useState(null)
  // Expanded parent ids. Default empty = all collapsed.
  // Persists across tab switches; an id present here means "user opened this branch".
  const [expanded, setExpanded] = useState(() => new Set())

  // MouseSensor + TouchSensor (separati, NON PointerSensor) — su touch il pointer
  // unificato confligge con lo scroll del browser. TouchSensor con delay sblocca lo
  // scroll fino a long-press attivato.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor),
  )

  const filtered = useMemo(
    () => data.categories.filter(c => c.type === tab && !isSystemCategory(c)),
    [data.categories, tab],
  )
  const rows = useMemo(
    () => buildTreeRows(filtered, { expanded }),
    [filtered, expanded],
  )

  function toggleExpand(id) {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function expand(id) {
    setExpanded(prev => {
      if (prev.has(id)) return prev
      const next = new Set(prev)
      next.add(id)
      return next
    })
  }

  const activeCategory = activeId ? data.categories.find(c => c.id === activeId) : null

  function handleDragEnd(event) {
    setActiveId(null)
    const { active, over } = event
    if (!over) return
    const draggedId = active.id

    if (over.id === TOP_LEVEL_DROP_ID) {
      if (canReparent(draggedId, null, data.categories)) {
        updateCategory(draggedId, { parentId: undefined })
      }
      return
    }
    if (canReparent(draggedId, over.id, data.categories)) {
      updateCategory(draggedId, { parentId: over.id })
    }
  }

  return (
    <div className="view view-categories">
      <div className="cat-toggle">
        <button
          type="button"
          className={`cat-toggle-btn expense${tab === 'expense' ? ' active' : ''}`}
          onClick={() => setTab('expense')}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="22 17 13.5 8.5 8.5 13.5 2 7"/>
            <polyline points="16 17 22 17 22 11"/>
          </svg>
          {t.categories.tabExpense}
        </button>
        <button
          type="button"
          className={`cat-toggle-btn income${tab === 'income' ? ' active' : ''}`}
          onClick={() => setTab('income')}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/>
            <polyline points="16 7 22 7 22 13"/>
          </svg>
          {t.categories.tabIncome}
        </button>
      </div>

      <button className="btn btn-primary cat-add-full" onClick={() => setEdit({})}>
        + {t.categories.add}
      </button>

      {rows.length === 0 ? (
        <div className="view-empty"><span>{t.categories.empty}</span></div>
      ) : (
        <DndContext
          sensors={sensors}
          onDragStart={(e) => setActiveId(e.active.id)}
          onDragCancel={() => setActiveId(null)}
          onDragEnd={handleDragEnd}
        >
          <TopLevelDropZone label={t.categories.parentNone} />
          <div className="list">
            {rows.map(({ cat, depth, hasChildren }) => (
              <DraggableRow
                key={cat.id}
                cat={cat}
                depth={depth}
                hasChildren={hasChildren}
                isExpanded={expanded.has(cat.id)}
                onToggle={() => toggleExpand(cat.id)}
                onAutoExpand={() => expand(cat.id)}
                isDragging={activeId === cat.id}
                onEdit={() => setEdit(cat)}
                allCategories={data.categories}
                draggedId={activeId}
              />
            ))}
          </div>
          <DragOverlay>
            {activeCategory ? (
              <div className="list-row drag-overlay">
                <div className="list-row-left">
                  <span className="cat-icon"><Emoji symbol={activeCategory.icon} size="1.4rem" /></span>
                  <div className="list-row-name">{activeCategory.name}</div>
                </div>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {editing && (
        <CategoryModal
          t={t}
          existing={editing.id ? editing : null}
          defaultType={tab}
          allCategories={data.categories}
          onTypeChange={setTab}
          onClose={() => setEdit(null)}
          onSubmit={(payload) => {
            if (editing.id) updateCategory(editing.id, payload)
            else addCategory(payload)
            setEdit(null)
          }}
          onDelete={() => {
            if (editing.id) {
              removeCategory(editing.id)
              setEdit(null)
            }
          }}
        />
      )}
    </div>
  )
}

function DraggableRow({
  cat, depth, hasChildren, isExpanded, onToggle, onAutoExpand,
  isDragging, onEdit, allCategories, draggedId,
}) {
  const { attributes, listeners, setNodeRef: setDragRef } = useDraggable({ id: cat.id })

  const acceptsDrop = draggedId && draggedId !== cat.id
    && canReparent(draggedId, cat.id, allCategories)

  const { isOver, setNodeRef: setDropRef } = useDroppable({
    id: cat.id,
    disabled: !acceptsDrop,
  })

  function setRefs(node) { setDragRef(node); setDropRef(node) }

  // Auto-expand when a drag hovers a collapsed parent for >700ms
  const expandTimer = useRef(null)
  useEffect(() => {
    if (isOver && hasChildren && !isExpanded && draggedId) {
      expandTimer.current = setTimeout(() => onAutoExpand(), 700)
    }
    return () => {
      if (expandTimer.current) {
        clearTimeout(expandTimer.current)
        expandTimer.current = null
      }
    }
  }, [isOver, hasChildren, isExpanded, draggedId, onAutoExpand])

  return (
    <div
      ref={setRefs}
      {...attributes}
      {...listeners}
      style={{ '--depth': depth }}
      className={[
        'list-row',
        depth > 0 ? 'has-parent' : '',
        isDragging ? 'is-dragging' : '',
        isOver ? 'drop-target' : '',
        hasChildren ? 'has-children' : '',
      ].filter(Boolean).join(' ')}
      onClick={onEdit}
      role="button"
      tabIndex={0}
    >
      <div className="list-row-left">
        <span className="drag-handle" aria-hidden="true">⋮⋮</span>
        <button
          type="button"
          className={`row-chevron${hasChildren ? '' : ' leaf'}`}
          onPointerDown={e => e.stopPropagation()}
          onClick={e => {
            e.stopPropagation()
            if (hasChildren) onToggle()
          }}
          aria-label={hasChildren ? (isExpanded ? 'Collapse' : 'Expand') : ''}
          tabIndex={hasChildren ? 0 : -1}
        >
          {hasChildren ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              {isExpanded
                ? <polyline points="6 9 12 15 18 9" />
                : <polyline points="9 6 15 12 9 18" />
              }
            </svg>
          ) : (
            <svg width="6" height="6" viewBox="0 0 6 6" aria-hidden="true">
              <circle cx="3" cy="3" r="2" fill="currentColor" />
            </svg>
          )}
        </button>
        <span className="cat-icon"><Emoji symbol={cat.icon} size="1.4rem" /></span>
        <div className="list-row-name">{cat.name}</div>
      </div>
    </div>
  )
}

function TopLevelDropZone({ label }) {
  const { isOver, setNodeRef } = useDroppable({ id: TOP_LEVEL_DROP_ID })
  return (
    <div
      ref={setNodeRef}
      className={`top-level-drop${isOver ? ' drop-target' : ''}`}
    >
      ↑ {label}
    </div>
  )
}

function CategoryModal({ t, existing, defaultType, allCategories, onTypeChange, onClose, onSubmit, onDelete }) {
  const [type, setType] = useState(existing?.type ?? defaultType)
  const defaultIcon = type === 'income' ? '💰' : '💸'
  const [icon, setIcon] = useState(existing?.icon ?? defaultIcon)
  const [name, setName] = useState(existing?.name ?? '')
  const [parentId, setParentId] = useState(existing?.parentId ?? '')
  const [confirmDelete, setConfirmDelete] = useState(false)

  // Eligible parents: same type, not self, not a descendant, and respect MAX_LEVELS
  // budget once `existing` is reparented under that candidate.
  const eligibleParents = useMemo(() => {
    if (!existing) {
      // Creating: any cat of same type whose depth + 1 ≤ MAX_LEVELS - 1
      return allCategories
        .filter(c => c.type === type && getDepth(c.id, allCategories) <= (MAX_LEVELS - 2))
        .sort((a, b) => getCategoryPath(a, allCategories).localeCompare(getCategoryPath(b, allCategories)))
    }
    const descendants = getDescendantIds(existing.id, allCategories)
    const subtree = getSubtreeDepth(existing.id, allCategories)
    return allCategories
      .filter(c =>
        c.type === type &&
        c.id !== existing.id &&
        !descendants.has(c.id) &&
        (getDepth(c.id, allCategories) + 1 + subtree) <= (MAX_LEVELS - 1),
      )
      .sort((a, b) => getCategoryPath(a, allCategories).localeCompare(getCategoryPath(b, allCategories)))
  }, [allCategories, type, existing])

  function submit(e) {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) return
    onSubmit({
      name: trimmed,
      type,
      icon: icon || defaultIcon,
      parentId: parentId || undefined,
    })
  }

  function changeType(newType) {
    setType(newType)
    onTypeChange?.(newType) // sync the page-level Uscite/Entrate tab below
    if (parentId) {
      const parent = allCategories.find(c => c.id === parentId)
      if (!parent || parent.type !== newType) setParentId('')
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <form className="modal" onClick={e => e.stopPropagation()} onSubmit={submit}>
        <h2>{existing ? t.categories.edit : t.categories.add}</h2>

        {!existing && (
          <div className="type-toggle" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={type === 'expense'}
              className={`type-toggle-btn${type === 'expense' ? ' active expense' : ''}`}
              onClick={() => changeType('expense')}
            >
              {t.categories.tabExpense}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={type === 'income'}
              className={`type-toggle-btn${type === 'income' ? ' active income' : ''}`}
              onClick={() => changeType('income')}
            >
              {t.categories.tabIncome}
            </button>
          </div>
        )}

        <div className="cat-head">
          <span className="emoji-current-display">
            <Emoji symbol={icon || defaultIcon} size="1.6rem" />
          </span>
          <input
            type="text" className="cat-name-input"
            placeholder={t.categories.namePlaceholder}
            value={name} onChange={e => setName(e.target.value)}
            autoFocus required
          />
        </div>

        <label>
          {t.categories.parent}
          <select value={parentId} onChange={e => setParentId(e.target.value)}>
            <option value="">{t.categories.parentNone}</option>
            {eligibleParents.map(p => (
              <option key={p.id} value={p.id}>
                {p.icon} {getCategoryPath(p, allCategories)}
              </option>
            ))}
          </select>
        </label>

        <EmojiPicker value={icon} onChange={setIcon} searchPlaceholder={t.common.searchIcon} />

        <div className="modal-actions">
          {existing && !confirmDelete && (
            <button
              type="button"
              className="btn btn-danger-outline"
              onClick={() => setConfirmDelete(true)}
            >
              {t.common.delete}
            </button>
          )}
          {existing && confirmDelete && (
            <button
              type="button"
              className="btn btn-danger"
              onClick={onDelete}
            >
              {t.categories.deleteConfirm}
            </button>
          )}
          <span style={{ flex: 1 }} />
          <button
            type="button"
            className="btn"
            onClick={confirmDelete ? () => setConfirmDelete(false) : onClose}
          >
            {t.common.cancel}
          </button>
          {!confirmDelete && (
            <button type="submit" className="btn btn-primary" disabled={!name.trim()}>
              {t.common.save}
            </button>
          )}
        </div>
      </form>
    </div>
  )
}
